import {
  Button,
  Card,
  CardContent,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  HelpCenter,
  Visibility,
  VisibilityOff,
  Wifi,
} from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

function isValidIPv4(str: string) {
  return /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)$/g.test(
    str
  );
}

function isValidSSID(str: string) {
  return /^[^!#;+\]/"\t][^+\]/"\t]{0,30}[^ +\]/"\t]$|^[^ !#;+\]/"\t]$[ \t]+$/g.test(
    str
  );
}

function isValidWPA2(str: string) {
  return /^[\u0020-\u007e]{8,63}$/g.test(str);
}

type NetworkWifiProps = {
  handleBack: () => void;
  handleNext: (configuration: any) => void;
};

const NetworkWifi: React.FC<NetworkWifiProps> = ({
  handleBack,
  handleNext,
}) => {
  const { t } = useTranslation();

  const [configuration, setConfiguration] = useState<any>({
    IFCS: [
      {
        ifc_name: "wifi",
        ifc_params: {
          ssid: "Default1o1",
          pwd: "TelwinLab101",
          dhcp: true,
          ip: "",
          mask: "",
          gateway: "",
          dns: "",
        },
      },
    ],
  });

  const [showPassword, setShowPassword] = useState(false);
  const handleShowPassword = () => setShowPassword(!showPassword);

  const checkFields = () => {
    if (
      !(
        configuration.IFCS[0].ifc_params.ssid &&
        configuration.IFCS[0].ifc_params.pwd &&
        isValidSSID(configuration.IFCS[0].ifc_params.ssid) &&
        isValidWPA2(configuration.IFCS[0].ifc_params.pwd)
      )
    ) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    console.log("NET.JSON", configuration);
  }, [configuration]);

  return (
    <Grid
      container
      spacing={2}
      direction="column"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
    >
      <Grid item>
        <Wifi style={{ fontSize: "64px" }} />
      </Grid>
      <Grid item>
        <Typography variant="h5">{t("wifiSettings")}</Typography>
      </Grid>
      <Grid item>
        <Typography>{t("wifiSettingsDescription")}</Typography>
      </Grid>
      <Grid
        container
        item
        spacing={4}
        alignItems="center"
        justifyContent="center"
        textAlign="left"
      >
        <Grid item xs={12} md={8} xl={6}>
          <Card variant="outlined">
            <CardContent style={{ margin: "24px" }}>
              <Grid container spacing={1} direction="column">
                <Grid item>
                  <Typography variant="h6">{t("mainNetwork")}</Typography>
                </Grid>
                <Grid item>
                  <TextField
                    onChange={(e) =>
                      setConfiguration({
                        ...configuration,
                        IFCS: [
                          {
                            ...configuration.IFCS[0],
                            ifc_params: {
                              ...configuration.IFCS[0].ifc_params,
                              ssid: e.target.value,
                            },
                          },
                        ],
                      })
                    }
                    value={configuration.IFCS[0].ifc_params.ssid}
                    fullWidth
                    size="small"
                    label={t("networkSsid")}
                    error={
                      !!configuration.IFCS[0].ifc_params.ssid &&
                      !isValidSSID(configuration.IFCS[0].ifc_params.ssid)
                    }
                    helperText={
                      !!configuration.IFCS[0].ifc_params.ssid &&
                      !isValidSSID(configuration.IFCS[0].ifc_params.ssid)
                        ? t("invalidSsid")
                        : undefined
                    }
                  />
                </Grid>
                <Grid item>
                  <TextField
                    onChange={(e) =>
                      setConfiguration({
                        ...configuration,
                        IFCS: [
                          {
                            ...configuration.IFCS[0],
                            ifc_params: {
                              ...configuration.IFCS[0].ifc_params,
                              pwd: e.target.value,
                            },
                          },
                        ],
                      })
                    }
                    value={configuration.IFCS[0].ifc_params.pwd}
                    fullWidth
                    size="small"
                    label={t("networkPassword")}
                    type={showPassword ? "text" : "password"}
                    error={
                      !!configuration.IFCS[0].ifc_params.pwd &&
                      !isValidWPA2(configuration.IFCS[0].ifc_params.pwd)
                    }
                    helperText={
                      !!configuration.IFCS[0].ifc_params.pwd &&
                      !isValidWPA2(configuration.IFCS[0].ifc_params.pwd)
                        ? t("invalidPassword")
                        : undefined
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title={`${t("showHidePassword")}`}>
                            <IconButton
                              onClick={handleShowPassword}
                              size="small"
                              style={{ marginRight: "8px" }}
                            >
                              {showPassword ? (
                                <VisibilityOff style={{ fontSize: "18px" }} />
                              ) : (
                                <Visibility style={{ fontSize: "18px" }} />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={`${t("onlyWpa2")}`}>
                            <IconButton size="small">
                              <HelpCenter style={{ fontSize: "18px" }} />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item>
                  <Typography variant="h6">{t("ipv4")}</Typography>
                </Grid>
                <Grid item>
                  <Switch
                    checked={!configuration.IFCS[0].ifc_params.dhcp}
                    onChange={() =>
                      setConfiguration({
                        ...configuration,
                        IFCS: [
                          {
                            ...configuration.IFCS[0],
                            ifc_params: {
                              ...configuration.IFCS[0].ifc_params,
                              dhcp: !configuration.IFCS[0].ifc_params.dhcp,
                            },
                          },
                        ],
                      })
                    }
                  />
                  {!configuration.IFCS[0].ifc_params.dhcp
                    ? " " + t("manual")
                    : " " + t("automaticDhcp")}
                </Grid>
                <Grid item>
                  <Collapse
                    in={!configuration.IFCS[0].ifc_params.dhcp}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Grid container spacing={1} direction="column">
                      <Grid item>
                        <TextField
                          onChange={(e) =>
                            setConfiguration({
                              ...configuration,
                              IFCS: [
                                {
                                  ...configuration.IFCS[0],
                                  ifc_params: {
                                    ...configuration.IFCS[0].ifc_params,
                                    ip: e.target.value,
                                  },
                                },
                              ],
                            })
                          }
                          value={configuration.IFCS[0].ifc_params.ip}
                          fullWidth
                          size="small"
                          label={t("ipAddress")}
                          error={
                            !!configuration.IFCS[0].ifc_params.ip &&
                            !isValidIPv4(configuration.IFCS[0].ifc_params.ip)
                          }
                          helperText={
                            !!configuration.IFCS[0].ifc_params.ip &&
                            !isValidIPv4(configuration.IFCS[0].ifc_params.ip)
                              ? t("invalidIpv4Address")
                              : undefined
                          }
                        />
                      </Grid>
                      <Grid item>
                        <TextField
                          onChange={(e) =>
                            setConfiguration({
                              ...configuration,
                              IFCS: [
                                {
                                  ...configuration.IFCS[0],
                                  ifc_params: {
                                    ...configuration.IFCS[0].ifc_params,
                                    mask: e.target.value,
                                  },
                                },
                              ],
                            })
                          }
                          value={configuration.IFCS[0].ifc_params.subnetMask}
                          fullWidth
                          size="small"
                          label={t("subnetMask")}
                          error={
                            !!configuration.IFCS[0].ifc_params.subnetMask &&
                            !isValidIPv4(
                              configuration.IFCS[0].ifc_params.subnetMask
                            )
                          }
                          helperText={
                            !!configuration.IFCS[0].ifc_params.subnetMask &&
                            !isValidIPv4(
                              configuration.IFCS[0].ifc_params.subnetMask
                            )
                              ? t("invalidIpv4Address")
                              : undefined
                          }
                        />
                      </Grid>
                      <Grid item>
                        <TextField
                          onChange={(e) =>
                            setConfiguration({
                              ...configuration,
                              IFCS: [
                                {
                                  ...configuration.IFCS[0],
                                  ifc_params: {
                                    ...configuration.IFCS[0].ifc_params,
                                    gateway: e.target.value,
                                  },
                                },
                              ],
                            })
                          }
                          value={configuration.IFCS[0].ifc_params.gateway}
                          fullWidth
                          size="small"
                          label={t("gateway")}
                          error={
                            !!configuration.IFCS[0].ifc_params.gateway &&
                            !isValidIPv4(
                              configuration.IFCS[0].ifc_params.gateway
                            )
                          }
                          helperText={
                            !!configuration.IFCS[0].ifc_params.gateway &&
                            !isValidIPv4(
                              configuration.IFCS[0].ifc_params.gateway
                            )
                              ? t("invalidIpv4Address")
                              : undefined
                          }
                        />
                      </Grid>
                      <Grid item>
                        <TextField
                          onChange={(e) =>
                            setConfiguration({
                              ...configuration,
                              IFCS: [
                                {
                                  ...configuration.IFCS[0],
                                  ifc_params: {
                                    ...configuration.IFCS[0].ifc_params,
                                    dns: e.target.value,
                                  },
                                },
                              ],
                            })
                          }
                          value={configuration.IFCS[0].ifc_params.dns}
                          fullWidth
                          size="small"
                          label={t("dns")}
                          error={
                            !!configuration.IFCS[0].ifc_params.dns &&
                            !isValidIPv4(configuration.IFCS[0].ifc_params.dns)
                          }
                          helperText={
                            !!configuration.IFCS[0].ifc_params.dns &&
                            !isValidIPv4(configuration.IFCS[0].ifc_params.dns)
                              ? t("invalidIpv4Address")
                              : undefined
                          }
                        />
                      </Grid>
                    </Grid>
                  </Collapse>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          <Grid
            container
            spacing={2}
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <Grid item>
              <Button
                disabled={configuration.hideBackButton}
                size="large"
                variant="outlined"
                style={{ float: "right", marginTop: "8px" }}
                onClick={handleBack}
              >
                {t("back")}
              </Button>
            </Grid>
            <Grid item>
              <Button
                disabled={!checkFields()}
                size="large"
                variant="contained"
                style={{ float: "right", marginTop: "8px" }}
                onClick={() => {
                  const filteredConfiguration = {
                    ...configuration,
                    IFCS: configuration.IFCS.map((ifc: any) => ({
                      ...ifc,
                      ifc_params: Object.fromEntries(
                        Object.entries(ifc.ifc_params).filter(
                          ([key, value]) => value !== ""
                        )
                      ),
                    })),
                  };
                  console.log("FILTERED", filteredConfiguration);
                  handleNext(filteredConfiguration);
                }}
              >
                {t("next")}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default NetworkWifi;
